import type { ApprovalRequest, FilePackage, FormTemplate, ClientUpdate } from '../api/contract'
/**
 * Screen DECISIONS, kept out of the components so they can be tested without a DOM.
 * Every function here encodes a rule that was once a real customer-visible bug.
 */
import { allowed, capabilityKnown, isResolutionDecidable, isReopenable, type Desk, type Issue, type InvitationView } from '../api/contract'

/** What the landing/invitation screen should say — one of four states, never the internal lifecycle enum. */
export function invitationState(inv: InvitationView | null, err?: string):
  { kind: 'error'; message: string } |
  { kind: 'acceptable' } |
  { kind: 'accepted' } |
  { kind: 'expired' } |
  { kind: 'unavailable' } {
  if (err) return { kind: 'error', message: err }
  if (!inv) return { kind: 'error', message: 'We could not find that invitation.' }
  if (inv.acceptable) return { kind: 'acceptable' }
  if (inv.status === 'accepted') return { kind: 'accepted' }
  if (inv.status === 'expired') return { kind: 'expired' }
  return { kind: 'unavailable' }
}

/**
 * Whether to render the approve / ask-to-reopen card, and each control inside it.
 *
 * `needsReread` is the whole point: on the LIST path the server sends no capability keys (it had no desk), so
 * the honest answer is "ask the server about this issue" — not "you may not". Rendering a disabled control
 * there is what removed the feature from every issue opened from a list.
 */
export function resolutionCard(issue: Issue): {
  show: boolean
  canApprove: boolean
  canReopen: boolean
  needsReread: boolean
  approved: boolean
  reopenPending: boolean
} {
  const decidable = isResolutionDecidable(issue)
  const reopenable = isReopenable(issue)
  // The SECTION shows whenever either half could apply. Gating it on `resolved` alone made the reopen control
  // unreachable on a CLOSED issue, which the server would have accepted.
  const show = decidable || reopenable
  const known = capabilityKnown(issue.canApproveResolution) || capabilityKnown(issue.canRequestReopen)
  // 🔴 Both flags read a DURABLE server fact, not a session flag. The screen used to show a thank-you held only
  // in component state: it vanished on reload and the customer was asked to confirm the same resolution again,
  // forever, because the capability was computed from the desk setting alone. Measured live before the fix.
  // `resolutionApprovedAt` is new (the server did not project it); `reopenRequestStatus` was ALREADY on the
  // wire and already declared in contract.ts — no screen simply ever read it.
  const approved = !!issue.resolutionApprovedAt
  const reopenPending = issue.reopenRequestStatus === 'pending'
  return {
    show,
    canApprove: decidable && allowed(issue.canApproveResolution) && !approved,
    canReopen: reopenable && allowed(issue.canRequestReopen) && !reopenPending,
    needsReread: show && !known,
    approved,
    reopenPending,
  }
}

/** The comment box: same absent-vs-false rule. */
/**
 * `locked` is a property of THIS THREAD; `canComment` is a property of the DESK. Both must allow it.
 *
 * 🔴 The box used to gate on `canComment` alone, so a closed thread still rendered a composer. The server
 * refuses with 409 `thread_locked`, which reached the customer as "Something went wrong on our side" — they
 * typed a message and were told the app was broken. That is precisely the "the only way they find out is by
 * pressing it" failure the capability keys exist to prevent, arriving through the one fact the client threw
 * away at the envelope boundary.
 *
 * ⚠️ There is NO UNLOCK: the thread transition map has no action producing `open`. Locked is terminal, so
 * this is a permanent state for that conversation, not a transient one worth optimistically retrying.
 */
export const commentBox = (issue: Issue, thread?: { locked: boolean }) => ({
  canComment: allowed(issue.canComment) && !(thread && thread.locked),
  needsReread: !capabilityKnown(issue.canComment),
  locked: !!(thread && thread.locked),
})

/** Customer-facing status wording. The server's enum is stable; this is only presentation. */
const STATUS_TEXT: Record<string, string> = {
  received: 'Received',
  in_review: 'Being looked at',
  in_progress: 'Being worked on',
  waiting_on_you: 'Waiting for your reply',
  resolved: 'Resolved — please confirm',
  declined: 'Not going ahead',
  closed: 'Closed',
}
export const statusLabel = (s: string): string => STATUS_TEXT[s] ?? 'In progress'

/**
 * The status as it should read for THIS issue, which is not always what the raw enum says.
 *
 * `resolved` maps to "Resolved — please confirm", which is a request. Once the customer HAS confirmed, leaving
 * it there put two contradictory sentences on one screen: the header asking them to confirm, the card telling
 * them they already had. (Introduced by the fix that made the confirmation visible at all — before it, the
 * whole screen consistently asked, because nothing knew the answer.) The enum is unchanged and still correct;
 * only the wording moves.
 */
export const statusLabelFor = (issue: Pick<Issue, 'publicStatus' | 'resolutionApprovedAt'>): string =>
  (issue.publicStatus === 'resolved' && issue.resolutionApprovedAt)
    ? 'Resolved — confirmed by you'
    : statusLabel(issue.publicStatus)

/**
 * The resolution note must render ONCE. The header and the resolution card read the same string, and printing
 * both showed the customer the same paragraph twice.
 */
export const resolutionNoteFor = (issue: Issue, place: 'header' | 'card'): string | null => {
  if (!issue.publicStatusNote) return null
  return place === (isResolutionDecidable(issue) ? 'card' : 'header') ? issue.publicStatusNote : null
}

/** Submit-form validation, mirroring what the desk itself declares. */
export function validateSubmission(desk: Desk, form: { title: string; description: string; issueType: string; severity: string; environment: string }): string[] {
  const errs: string[] = []
  if (form.title.trim().length < 4) errs.push('Please give the issue a short title.')
  if (form.description.trim().length < 10) errs.push('Please describe what happened, in a sentence or two.')
  if (desk.issueTypes.length && !desk.issueTypes.includes(form.issueType)) errs.push('Please choose what kind of issue this is.')
  if (desk.severities.length && !desk.severities.includes(form.severity)) errs.push('Please choose how badly this affects you.')
  if (desk.requireEnvironment && !form.environment.trim()) errs.push('Please tell us where this happened (which browser, device or site).')
  return errs
}


/* ══ WORKSPACE SURFACES ══ decisions kept out of the components so they can be tested without a DOM. */

/**
 * 🔴 A PACKAGE MAY BE VISIBLE AND NOT DOWNLOADABLE, and the two must not render the same.
 *
 * `allowedActions` is the host's decision about what this guest may DO with a package they can already SEE
 * (view-only sharing is a real configuration). Offering a download button that the server then refuses is
 * the class of defect this portal keeps finding — a control for an action that cannot succeed.
 *
 * ABSENT is not permission. An older package with no `allowedActions` array is treated as view-only rather
 * than assumed downloadable, because the failure directions are not symmetric: a missing button is a
 * question the customer can ask, a broken one is a bug report.
 */
export const canDownload = (pkg: Pick<FilePackage, 'allowedActions' | 'fileCount'>): boolean =>
  Array.isArray(pkg.allowedActions) && pkg.allowedActions.indexOf('download') !== -1 && (pkg.fileCount || 0) > 0

/**
 * The responses to OFFER, straight from the approval's own list.
 *
 * 🔴 NEVER a fixed approve/reject pair. `guestRespondApproval` refuses anything outside
 * `allowedResponses` with `response_not_allowed`, and the set is per approval — an acknowledgement has one
 * option, a sign-off has two or three. Rendering a guessed pair means half the buttons 4xx.
 */
export function approvalOptions(a: Pick<ApprovalRequest, 'allowedResponses' | 'status' | 'responseRequiredBy'>, now = new Date()): {
  options: string[]; closed: boolean; reason: string | null
} {
  const options = Array.isArray(a.allowedResponses) ? a.allowedResponses.filter((x) => !!x) : []
  // The server checks these in this order and answers a different code for each; mirroring it means the
  // screen explains the same thing the API would have.
  if (a.status && a.status !== 'active') return { options: [], closed: true, reason: 'You have already responded to this.' }
  if (a.responseRequiredBy && new Date(a.responseRequiredBy).getTime() <= now.getTime()) {
    return { options: [], closed: true, reason: 'The deadline for responding has passed.' }
  }
  if (!options.length) return { options: [], closed: true, reason: 'No response options were set for this request.' }
  return { options, closed: false, reason: null }
}

/**
 * Which required fields are still empty.
 *
 * Mirrors `buildAnswers` on the server, which drops any value that is null/undefined/empty-string and then
 * fails the whole submission if a required field ended up absent. Checking it here turns a rejected POST
 * into an inline message naming the field.
 */
export function missingRequired(template: Pick<FormTemplate, 'fields'>, values: Record<string, string>): string[] {
  const fields = Array.isArray(template.fields) ? template.fields : []
  return fields
    .filter((f) => f && f.required && !(values[f.fieldId] != null && String(values[f.fieldId]).length > 0))
    .map((f) => f.label || f.fieldId)
}

/** A quote needs a title and nothing else; the server derives every reference from the RFQ and the session. */
export const quoteReady = (v: { quoteTitle: string }): boolean => !!v.quoteTitle && v.quoteTitle.trim().length > 0

/**
 * ⚠️ ACKNOWLEDGED IS A DURABLE FACT, not a session flag — `acknowledgedAt` comes back on every read. The
 * issue screen learned this the hard way: it thanked the customer for confirming and then asked again on
 * the next load, because the confirmation lived only in component state.
 */
export const updateAcknowledged = (u: Pick<ClientUpdate, 'acknowledgedAt'>): boolean => !!u.acknowledgedAt